import type { Item, PityState, SaveData } from '../model/types';
import { applyOfflineToSave, computeOfflineGains, zeroRates, type OfflineGains } from '../sim/offline';

/** Matches the planned heartbeat timeout: shorter gaps are not an offline session. */
export const SESSION_OFFLINE_GRACE_MS = 60_000;

export interface OfflineSummary {
  gains: OfflineGains;
  levelsGained: number;
  keptItems: Item[];
  salvaged: number;
  dustGained: number;
  goldGained: number;
  xpGained: number;
}

/**
 * Claims offline gains from server timestamps already stored on the save.
 * Does not read the device clock. A gap under the heartbeat grace is discarded
 * unless this is an explicit local preview.
 */
export function claimIfDue(args: {
  data: SaveData;
  serverNowMs: number;
  explicitPreview?: boolean;
  rollItems?: (count: number, pity: PityState) => { items: Item[]; pity: PityState };
}): { data: SaveData; summary: OfflineSummary | null } {
  const data = structuredClone(args.data);
  if (!data.idle.pendingClaim || data.idle.lastActiveAtMs === null) {
    return { data, summary: null };
  }

  const raw = args.serverNowMs - data.idle.lastActiveAtMs;
  if (!args.explicitPreview && raw < SESSION_OFFLINE_GRACE_MS) {
    data.idle.pendingClaim = false;
    data.idle.lastActiveAtMs = null;
    return { data, summary: null };
  }

  const snapshot = data.idle.snapshot;
  const farmStageId = snapshot?.farmStageId ?? 0;
  const gains = computeOfflineGains({
    rates: farmStageId > 0 && snapshot ? snapshot.rates : zeroRates(),
    farmStageId,
    heroLevel: data.hero.level,
    heroXp: data.hero.xp,
    serverNowMs: args.serverNowMs,
    lastActiveAtMs: data.idle.lastActiveAtMs,
    capHours: snapshot?.capHours,
    pity: data.gear.pity,
    rollItems: farmStageId > 0 ? args.rollItems : undefined,
  });

  const beforeDust = data.currencies.forgeDust;
  const beforeGold = data.currencies.gold;
  const beforeLevel = data.hero.level;
  const next = applyOfflineToSave(data, gains);
  next.idle.pendingClaim = false;
  next.idle.lastActiveAtMs = null;

  const keptIds = new Set(next.gear.bag.map((item) => item.id));
  const keptItems = gains.items.filter((item) => keptIds.has(item.id));
  return {
    data: next,
    summary: {
      gains,
      levelsGained: next.hero.level - beforeLevel,
      keptItems,
      salvaged: gains.items.length - keptItems.length,
      dustGained: next.currencies.forgeDust - beforeDust,
      goldGained: next.currencies.gold - beforeGold,
      xpGained: gains.xp,
    },
  };
}
